<template>
  <div>
    <div v-if="usb" data-test-id="usb-form-header" class="bg-white py-3 py-md-5">
      <b-container>
        <b-link to="/" class="font-condensed d-inline-flex align-items-center mb-3">
          <b-icon icon="bobcaticon-up" class="fa-rotate-270 text-small mr-2" />
          {{ 'backButton' | translate(labels, 'servicelibrary_USBFormComponent') }}
        </b-link>
        <div class="d-flex flex-wrap">
          <h1 class="col-12 col-md-auto p-0">{{ 'componentTitle' | translate(labels, 'servicelibrary_USBFormComponent') }}</h1>
          <b-btn variant="primary" class="ml-md-4 col-12 col-md-auto" @click="openModal('formModal')">
            {{ 'requestChangesButton' | translate(labels, 'servicelibrary_USBFormComponent') }}
          </b-btn>
        </div>
        <b-link
          class="font-condensed d-inline-flex align-items-center mt-3"
          @click="openModal('tacModal')"
        >
          <b-icon icon="bobcaticon-clipboard" class="text-small mr-2" />
          {{ 'termsAndConditionsLabel' | translate(labels, 'servicelibrary_USBFormComponent') }}
        </b-link>
        <b-link
          v-if="support"
          data-test-id="usb-form-support"
          class="font-condensed d-inline-flex align-items-center mt-3 ml-4"
          :href="translatedLabel('troubleshootingLink', 'servicelibrary_USBFormComponent')"
        >
          <b-icon icon="question-circle" class="text-small mr-2" />
          {{ 'troubleshootingSupport' | translate(labels, 'servicelibrary_USBFormComponent') }}
        </b-link>
      </b-container>
    </div>
    <b-container class="py-3 py-md-4">
      <div v-if="usb" data-test-id="usb-form-subscriber-list" class="p-3 px-md-5 py-md-4 bg-white maxw-50">
        <h2>{{ 'subscriberListTitle' | translate(labels, 'servicelibrary_USBFormComponent') }}</h2>
        <overlay-wrapper :show="subscriptionsLoading">
          <BError v-if="subscriptionsError" :code="subscriptionsError.code">
            {{ subscriptionsErrorMessage }}
          </BError>

          <div v-else-if="!subscriptionsLoading && !subscriptions.length" class="cols-12 col-md-8 border-0 text-center text-black bg-white m-auto pt-5">
            {{ 'noDealersFound' | translate(labels, 'servicelibrary_USBFormComponent') }}
          </div>

          <BasicGridTable v-else :disabled="subscriptionsLoading" header-class="d-none d-md-grid" grid-name="subscriptions-list">
            <template slot="header">
              <BasicGridTableHeader v-for="f of fields"
                                    :key="'sub__' + f.key"
                                    :name="f.key"
              >
                {{ f.label }}
              </BasicGridTableHeader>
            </template>

            <template slot="rows">
              <div v-for="subscription of subscriptions"
                   :key="'item__' + subscription.ar_number"
                   class="grid-table-row position-relative overflow-hidden inactive"
              >
                <div class="sub__number mb-3 mb-md-0">
                  <div class="d-block d-md-none text-small text-gray-800">{{ fields[0].label }}</div>
                  <div class="text-black">{{ subscription.ar_number }}</div>
                </div>

                <div class="sub__name mb-3 mb-md-0">
                  <div class="d-block d-md-none text-small text-gray-800">{{ fields[1].label }}</div>
                  <div class="text-black overflow-wrap">{{ subscription.dealership }}</div>
                </div>

                <div class="sub__current_year mb-3 mb-md-0">
                  <div class="d-block d-md-none text-small text-gray-800">{{ fields[2].label }}</div>
                  <div class="text-black">{{ subscription.qty_current_year }}</div>
                </div>

                <div class="sub__next_year mb-3 mb-md-0">
                  <div class="d-block d-md-none text-small text-gray-800">{{ fields[3].label }}</div>
                  <div class="text-black">{{ subscription.qty_next_year }}</div>
                </div>

                <div class="sub__modified mb-2 mb-md-0">
                  <div class="d-block d-md-none text-small text-gray-800">{{ fields[4].label }}</div>
                  <div class="text-black">{{ subscription.modified | formatDate }}</div>
                </div>
              </div>
            </template>
          </BasicGridTable>
        </overlay-wrapper>
      </div>
      <BError v-else-if="usbError" data-test-id="usb-form-error">{{ 'featureUnauthorized' | translate(labels,'servicelibrary_USBFormComponent') }}</BError>
      <div v-else class="bc-spinner" data-test-id="usb-form-spinner" />
    </b-container>

    <b-modal ref="formModal" :title="translatedLabel('formTitle', 'servicelibrary_USBFormComponent')" @hide="resetForm">
      <b-form class="px-3 px-md-5" @submit="onSubmit">
        <b-row class="bg-gray-100 m-0 mb-3 py-3">
          <b-col cols="12" md="6">
            <b-form-group :label="translatedLabel('requestTypeLabel', 'servicelibrary_USBFormComponent')">
              <b-form-invalid-feedback id="request_type-feedback" :state="validateState('request_type')" class="mb-3 mt-0">
                {{ 'requestTypeInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}
              </b-form-invalid-feedback>
              <b-form-radio-group
                id="request_type-group"
                v-model="$v.form.request_type.$model"
                :state="validateState('request_type')"
                name="request_type"
                aria-describedby="request_type-feedback"
              >
                <b-form-radio class="d-block mb-2" value="ADD">{{ 'requestTypeAdd' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-form-radio>
                <b-form-radio class="d-block mb-2" value="CHANGE">{{ 'requestTypeChange' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-form-radio>
                <b-form-radio class="d-block mb-2" value="CANCEL">{{ 'requestTypeCancel' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <label for="subscriptions_input">{{ 'subscriptionsLabel' | translate(labels, 'servicelibrary_USBFormComponent') }}</label>
            <div class="text-small mb-3" v-html="translatedLabel('subscriptionsPriceContent', 'servicelibrary_USBFormComponent')" />
            <div class="spinbutton-wrapper position-relative">
              <div class="spinbutton-value position-absolute">{{ $v.form.subscription_count.$model }}</div>
              <b-spinbutton
                id="subscriptions_input"
                v-model="$v.form.subscription_count.$model"
                :disabled="form.request_type === 'CANCEL'"
                :min="subscription_count_min"
                :max="subscription_count_max"
                aria-describedby="subscription_count-feedback"
              />
            </div>
            <b-form-invalid-feedback id="subscription_count-feedback" :state="form.subscription_count !== 10">
              {{ 'subscriptionsInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}
            </b-form-invalid-feedback>
          </b-col>
        </b-row>

        <div class="row d-block position-relative zindex-2">
          <b-form-group
            id="ar-group"
            class="form-ar-item px-3 col-12 col-md-6 float-left"
            :label="translatedLabel('shipToARLabel', 'servicelibrary_USBFormComponent')"
            label-for="ship-ar"
            aria-describedby="ship_to_ar-feedback"
          >
            <basic-filter-item
              id="ship_to_ar"
              ref="ship_to_ar"
              class="wide dropdown m-0"
              :disabled="descendants.length === 1"
              :class="{'is-invalid': validateState('ship_to_ar') === false}"
            >
              <template slot="title">
                <div class="text-truncate pr-2">{{ shipToArTitle[0] }}</div>
                <div v-if="shipToArTitle.length === 2">{{ shipToArTitle[1] }}</div>
              </template>
              <div
                v-for="option in shipToArOptions"
                :key="option.value"
                class="nav-item text-nowrap"
                @click="$refs.ship_to_ar && $refs.ship_to_ar.hideDropdown()"
              >
                <b-dropdown-item @click="$v.form.ship_to_ar.$model = option.value">
                  {{ option.name }}
                </b-dropdown-item>
              </div>
            </basic-filter-item>

            <b-form-invalid-feedback id="ship_to_ar-feedback" :state="validateState('ship_to_ar')">
              <b-icon icon="exclamation-triangle" class="mr-1" />
              <template v-if="!$v.form.ship_to_ar.notGM">{{ 'GMARInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}</template>
              <template v-else>{{ 'shipToARInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            id="shippingAddress-group"
            class="form-ar-item px-3 col-12 col-md-6 float-right"
            :label="translatedLabel('shippingAddressLabel', 'servicelibrary_USBFormComponent')"
            label-for="shippingAddress"
          >
            <div id="shippingAddress" class="bg-gray-100 text-small p-2 d-flex flex-column justify-content-between">
              <i v-if="ship_to_address" v-html="ship_to_address" />
              <i v-else v-html="translatedLabel('shippingAddressInvalidFeedback', 'servicelibrary_USBFormComponent')" />
              <p class="m-0 mt-2">{{ 'shippingAddressUpdateContent' | translate(labels, 'servicelibrary_USBFormComponent') }}</p>
            </div>
          </b-form-group>

          <b-form-group
            id="ar-group-bill"
            class="form-ar-item px-3 col-12 col-md-6 float-left"
            :label="translatedLabel('billToARLabel', 'servicelibrary_USBFormComponent')"
            label-for="bill-ar"
          >
            <basic-filter-item
              id="bill_to_ar"
              ref="bill_to_ar"
              class="wide dropdown m-0"
              :disabled="descendants.length === 1"
              :class="{'is-invalid': validateState('bill_to_ar') === false}"
            >
              <template slot="title">
                <div class="text-truncate pr-2">{{ billToArTitle[0] }}</div>
                <div v-if="billToArTitle.length === 2">{{ billToArTitle[1] }}</div>
              </template>
              <div
                v-for="option in shipToArOptions"
                :key="option.value"
                class="nav-item text-nowrap"
                :class="{'disabled': option.disabled}"
                @click="$refs.bill_to_ar && $refs.bill_to_ar.hideDropdown()"
              >
                <b-dropdown-item @click="$v.form.bill_to_ar.$model = option.value">
                  <span>{{ option.name }}</span>
                </b-dropdown-item>
              </div>
            </basic-filter-item>

            <b-form-invalid-feedback id="bill_to_ar-feedback" :state="validateState('bill_to_ar')">
              <b-icon icon="exclamation-triangle" class="mr-1" />
              <template v-if="!$v.form.bill_to_ar.notGM">{{ 'GMARInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}</template>
              <template v-else>{{ 'shipToARInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-row class="clearfix">
          <b-col cols="12" md="6">
            <b-form-group
              id="requestor-name-group"
              :label="translatedLabel('requestorNameLabel', 'servicelibrary_USBFormComponent')"
              label-for="requestor-name"
            >
              <input
                id="requestor-name"
                v-model="$v.form.requestor_name.$model"
                class="form-control"
                disabled
                :placeholder="translatedLabel('requestorNamePlaceholder', 'servicelibrary_USBFormComponent')"
                aria-describedby="requestor_name-feedback"
                :class="{'is-invalid':validateState('requestor_name') === false}"
                trim
              >
              <b-form-invalid-feedback id="requestor_name-feedback" :state="validateState('requestor_name')"><b-icon icon="exclamation-triangle" class="mr-1" />{{ 'requestorNameInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              id="requestor-email-group"
              :label="translatedLabel('requestorEmailLabel', 'servicelibrary_USBFormComponent')"
              label-for="requestor-email"
            >
              <input
                id="requestor-email"
                v-model="$v.form.requestor_email.$model"
                class="form-control"
                disabled
                :placeholder="translatedLabel('requestorEmailPlaceholder', 'servicelibrary_USBFormComponent')"
                aria-describedby="requestor_email-feedback"
                trim
              >
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          id="dealer-order-group"
          :label="translatedLabel('dealerPOLabel', 'servicelibrary_USBFormComponent')"
          label-for="dealer-po"
        >
          <input
            id="dealer-po"
            v-model="$v.form.dealer_po.$model"
            class="form-control"
            aria-describedby="dealer_po-feedback"
            :placeholder="translatedLabel('dealerPOPlaceholder', 'servicelibrary_USBFormComponent')"
            trim
          >
        </b-form-group>

        <b-form-group
          id="comments-group"
          :label="translatedLabel('commentsLabel', 'servicelibrary_USBFormComponent')"
          label-for="comments"
        >
          <textarea
            id="comments"
            v-model="$v.form.comments.$model"
            class="form-control"
            :placeholder="translatedLabel('commentsPlaceholder', 'servicelibrary_USBFormComponent')"
            aria-describedby="comments-feedback"
            rows="3"
            max-rows="6"
          />
        </b-form-group>

        <b-form-group
          id="acceptance-group"
        >
          <b-form-checkbox
            id="acceptance"
            v-model="$v.form.terms_accepted.$model"
            name="terms_accepted"
            class="text-small d-inline-flex"
            aria-describedby="terms_accepted-feedback"
            :state="validateState('terms_accepted')"
          >
            {{ 'acceptanceLabel' | translate(labels, 'servicelibrary_USBFormComponent') }}
          </b-form-checkbox>
          <b-link class="font-condensed text-small ml-1" @click="openModal('tacModal')">{{ 'termsAndConditionsLabel' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-link>
          <b-form-invalid-feedback id="terms_accepted-feedback" :state="validateState('terms_accepted')"><b-icon icon="exclamation-triangle" class="mr-1" />{{ 'acceptanceInvalidFeedback' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-form-invalid-feedback>
        </b-form-group>

        <BError v-if="error" :code="error.code">
          {{ error.message }}
        </BError>
      </b-form>

      <template #modal-footer>
        <div class="text-right  d-flex flex-wrap d-md-block w-100">
          <b-button class="mt-2  order-2 col-12 col-md-auto" variant="link" :disabled="loading" @click="resetForm()">{{ 'resetFormButton' | translate(labels, 'shared_CommonFunctional') }}</b-button>
          <b-button
            class="mt-2 ml-md-3 order-1 col-12 col-md-auto"
            variant="outline-primary"
            :disabled="loading"
            @click="closeModal('formModal')"
          >
            {{ 'closeButton' | translate(labels, 'shared_CommonFunctional') }}
          </b-button>
          <b-button type="submit" :disabled="loading" variant="primary" class="mt-2 ml-md-3 order-0 col-12 col-md-auto" @click="onSubmit"><b-spinner v-if="loading" small class="mr-2" />{{ 'submitButton' | translate(labels, 'servicelibrary_USBFormComponent') }}</b-button>
        </div>
      </template>
    </b-modal>

    <b-modal ref="tacModal" ok-only :ok-title="translatedLabel('closeButton', 'shared_CommonFunctional')" footer-class="ok-only" :title="translatedLabel('termsAndConditionsTitle', 'servicelibrary_USBFormComponent')">
      <div class="px-3 px-md-5 pt-4">
        <div v-html="translatedLabel('termsAndConditionsContent', 'servicelibrary_USBFormComponent')" />
      </div>
    </b-modal>

    <b-modal ref="successModal" ok-only :ok-title="translatedLabel('closeButton', 'shared_CommonFunctional')" footer-class="ok-only" :title="translatedLabel('successModalTitle', 'servicelibrary_USBFormComponent')">
      <div class="px-3 px-md-5 pt-4">
        <div v-html="translatedLabel('successModalContent', 'servicelibrary_USBFormComponent')" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import BError from '@/components/ui/BError'
import {validationMixin} from 'vuelidate'
import {mapGetters} from 'vuex'
import {required, minValue, maxValue, sameAs, helpers} from 'vuelidate/lib/validators'
import {dpmmService} from '@/services'
import BasicGridTable from '@/components/ui/BasicGridTable'
import BasicGridTableHeader from '@/components/ui/BasicGridTableHeader'
import BasicFilterItem from '@/components/ui/forms/BasicFilterItem'
const contains = (param) =>
  (value) => !helpers.req(value) || param.indexOf(value) >= 0
const notContains = (param) =>
  (value) => param.indexOf(value) === -1
export default {
  conceptName: ['servicelibrary_USBFormComponent', 'shared_CommonFunctional', 'shared_CommonErrors'],
  components: {
    BError,
    BasicGridTable,
    BasicGridTableHeader,
    BasicFilterItem
  },
  mixins: [validationMixin],
  props: {
    usb: {
      default: false
    },
    usbError: {
      default: false
    },
    support: {
      default: false
    }
  },
  data() {
    return {
      modal: false,
      identity: {},
      descendants: [],
      availableAR: [],
      GMAR: [],
      loading: false,
      error: null,
      subscription_count_is_valid: true,
      subscription_count_min: 1,
      subscription_count_max: 10,
      ship_to_address: '',
      form: {
        'request_type': null,
        'subscription_count': 1,
        'requestor_name': null,
        'requestor_email': null,
        'ship_to_ar': null,
        'bill_to_ar': null,
        'dealer_po': null,
        'comments': null,
        'terms_accepted': false
      },
      subscriptionsLoading: false,
      subscriptions: [],
      subscriptionsError: false
    }
  },
  computed: {
    ...mapGetters({
      getRegion: 'servicelibrary/getRegion'
    }),
    subscriptionsErrorMessage() {
      if (!this.subscriptionsError) return ''
      if (this.subscriptionsError.code == 404) return this.$options.filters.translate('usbFormNotAvailable', this.labels, 'shared_CommonErrors')
      return this.subscriptionsError.message
    },
    fields() {
      return [
        {key: 'number', label: this.$options.filters.translate('ARNumber', this.labels, 'servicelibrary_USBFormComponent'), sortable: false},
        {key: 'name', label: this.$options.filters.translate('dealershipName', this.labels, 'servicelibrary_USBFormComponent'), sortable: false},
        {key: 'current_year', label: this.$options.filters.translate('currentYearSubscriptions', this.labels, 'servicelibrary_USBFormComponent'), sortable: false},
        {key: 'next_year', label: this.$options.filters.translate('nextYearSubscriptions', this.labels, 'servicelibrary_USBFormComponent'), sortable: false},
        {key: 'modified', label: this.$options.filters.translate('lastUpdated', this.labels, 'servicelibrary_USBFormComponent'), sortable: false}
      ]
    },
    shipToArOptions() {
      return this.descendants.map(({arNumber, name}) => ({
        value: arNumber,
        name: `${name} - ${arNumber}`
      }))
    },
    shipToArTitle(){
      const currentValue = this.$v.form.ship_to_ar.$model
      const item = this.descendants.find(({arNumber}) => arNumber === currentValue)
      if (item) return [item.name, item.arNumber]
      return [this.translatedLabel('shipToARPlaceholder', 'servicelibrary_USBFormComponent')]
    },
    billToArTitle() {
      const currentValue = this.$v.form.bill_to_ar.$model
      const item = this.descendants.find(({arNumber}) => arNumber === currentValue)
      if (item) return [item.name, item.arNumber]
      return [this.translatedLabel('billToARPlaceholder', 'servicelibrary_USBFormComponent')]
    }
  },
  watch: {
    'form.request_type'(n) {
      if (n === 'CANCEL') {
        this.form.subscription_count = 0
        this.subscription_count_min = 0
        this.subscription_count_max = 0
      } else {
        if (this.form.subscription_count === 0) this.form.subscription_count = 1
        this.subscription_count_min = 1
        this.subscription_count_max = 10
      }
    },
    'form.subscription_count'(n) {
      if (n > 10) {
        this.form.subscription_count = 10
        this.$v.form.subscription_count.$model = 10
        this.subscription_count_is_valid = false
      }
    },
    'form.ship_to_ar'(n) {
      const found = this.descendants.find(function (element) {
        return element.arNumber === n
      })
      if (found) {
        this.fillAddress(found)
      } else {
        this.ship_to_address = ''
      }
    }
  },
  validations() {
    return {
      form: {
        request_type: {required},
        subscription_count: {
          required,
          minValue: minValue(this.subscription_count_min),
          maxValue: maxValue(this.subscription_count_max)
        },
        ship_to_ar: {required, validAR: contains(this.availableAR), notGM: notContains(this.GMAR)},
        bill_to_ar: {validAR: contains(this.availableAR), notGM: notContains(this.GMAR)},
        requestor_name: {required},
        requestor_email: {},
        dealer_po: {},
        comments: {},
        terms_accepted: {sameAs: sameAs(() => true)}
      }
    }
  },
  beforeMount() {
    dpmmService.fetchIdentity().then(response => {
      this.identity = response.data
      this.preFillForm()
    })

    dpmmService.fetchOrgDescendants().then(response => {
      for (const item of response.data) {
        if (item.type === 'DEALER') this.descendants.push(item)
        if (item.dealerContractType === 'Grounds Maintenance') this.GMAR.push(item)
      }
      this.availableAR = this.descendants.map(descendant => descendant.arNumber)
      this.GMAR = this.GMAR.map(item => item.arNumber)
      this.preFillForm()
    })
  },
  created() {
    this.fetchSubscriptions()
  },
  methods: {
    fetchSubscriptions() {
      this.subscriptionsError = false
      this.subscriptionsLoading = true
      dpmmService.getSubscriptions(this.getRegion).then((r) => {
        if (r && r.data && r.data.subscriptions && r.data.subscriptions.length) {
          this.subscriptions = r.data.subscriptions
          this.subscriptions.sort(function (a, b) {
            const textA = a.dealership.toUpperCase()
            const textB = b.dealership.toUpperCase()
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
          })
        }
      }).catch((e) => {
        this.subscriptionsError = {message: e.message, code: e.code}
      }).finally(() => {
        this.subscriptionsLoading = false
      })
    },
    preFillForm() {
      this.form.requestor_name = (this.identity.firstName || '') + ' ' + (this.identity.lastName || '')
      this.form.requestor_email = this.identity.email || ''
      if (this.descendants.length === 1) {
        this.fillAddress(this.descendants[0])
      }
    },
    fillAddress(descendant) {
      this.ship_to_address = descendant.primaryShipToLine1 + '<br/>' + descendant.primaryShipToCity + ', ' + descendant.primaryShipToState + ' ' + descendant.primaryShipToPostalCode
      this.$v.form.ship_to_ar.$model = descendant.arNumber
    },
    validateState(name) {
      const {$dirty, $error} = this.$v.form[name]
      return $dirty && $error ? false : null
    },
    resetForm() {
      this.form = {
        'request_type': null,
        'subscription_count': 1,
        'requestor_name': null,
        'ship_to_ar': null,
        'bill_to_ar': null,
        'dealer_po': null,
        'comments': null,
        'terms_accepted': false
      }

      this.$nextTick(() => {
        this.$v.$reset()
        this.preFillForm()
      })
    },
    onSubmit() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        return
      }

      this.error = null
      this.loading = true
      dpmmService.sendSubscriptions(this.form).then(() => {
        this.loading = false
        this.closeModal('formModal')
        this.openModal('successModal')
        this.resetForm()
      }).catch((r) => {
        this.loading = false
        this.error = {message: r.message, code: r.code}
      })
    },
    openModal(modalId) {
      this.$refs[modalId].show()
    },
    closeModal(modalId) {
      this.$refs[modalId].hide()
    }
  }
}
</script>

<style lang="scss">
#subscriptions_input {
  bdi {
    color: transparent;
  }
}

#acceptance + label {
  line-height: 1.5rem;
}
</style>

<style lang="scss" scoped>

.form-control.is-invalid {
  background-image: none;
}

.clearfix {
  clear: both;
}

.spinbutton-value {
  text-align: center;
  pointer-events: none;
  top: .5rem;
  left: 0;
  right: 0;
}
.spinbutton-wrapper {
  width: 7rem;
}

#shippingAddress {
  min-height: 8rem;
}

.maxw-50 {
  max-width: 50rem;
}
</style>
